import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Package,
  Wrench,
  MapPin,
  ArrowRight,
  History,
  X,
  Loader2,
  Box,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { yardApi, YardAssetSearchResult } from '@/api/services/yard.api';
import { PageHeader } from '@/components/common/PageHeader';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

interface RecentSearch {
  query: string;
  timestamp: Date;
  resultType?: string;
}

export default function YardAssetSearch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<YardAssetSearchResult | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('yardAssetSearchHistory');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setRecentSearches(parsed.map((s: any) => ({ ...s, timestamp: new Date(s.timestamp) })));
      } catch (e) {
        console.error('Error parsing search history:', e);
      }
    }
  }, []);

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Search if query param exists

  const saveSearch = useCallback(
    (query: string, resultType?: string) => {
      const newSearch: RecentSearch = {
        query,
        timestamp: new Date(),
        resultType,
      };

      const updated = [newSearch, ...recentSearches.filter((s) => s.query !== query)].slice(0, 10);

      setRecentSearches(updated);
      localStorage.setItem('yardAssetSearchHistory', JSON.stringify(updated));
    },
    [recentSearches]
  );
  const handleSearch = useCallback(
    async (query?: string) => {
      const searchTerm = query || searchQuery.trim();
      if (!searchTerm) {
        toast.error('Please enter a search term');
        return;
      }

      setSearching(true);
      setNotFound(false);
      setSearchResult(null);

      try {
        const response = await yardApi.searchAssets(searchTerm);
        if (response.results && response.results.length > 0) {
          // Take the first result as the primary match
          const result = response.results[0];
          setSearchResult(result);
          saveSearch(searchTerm, result.type);
          setSearchParams({ q: searchTerm });
        } else {
          setNotFound(true);
          saveSearch(searchTerm);
        }
      } catch (err: unknown) {
        const error = err as { response?: { status?: number } };
        if (error.response?.status === 404) {
          setNotFound(true);
          saveSearch(searchTerm);
        } else {
          console.error('Search error:', error);
          toast.error('Search failed. Please try again.');
        }
      } finally {
        setSearching(false);
      }
    },
    [saveSearch, searchQuery, setSearchParams]
  );

  useEffect(() => {
    const q = searchParams.get('q');
    if (q && q !== searchQuery) {
      setSearchQuery(q);
      handleSearch(q);
    }
  }, [searchParams, searchQuery, handleSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRecentSearchClick = (query: string) => {
    setSearchQuery(query);
    handleSearch(query);
  };

  const clearSearchHistory = () => {
    setRecentSearches([]);
    localStorage.removeItem('yardAssetSearchHistory');
    toast.success('Search history cleared');
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResult(null);
    setNotFound(false);
    setSearchParams({});
    inputRef.current?.focus();
  };

  const navigateToDetail = () => {
    if (!searchResult) return;

    switch (searchResult.type) {
      case 'INVENTORY_ITEM':
        navigate(`/inventory/${searchResult.id}`);
        break;
      case 'EQUIPMENT':
        navigate(`/equipment/${searchResult.id}`);
        break;
      case 'STORAGE_LOCATION':
        navigate(`/yard/locations?selected=${searchResult.id}`);
        break;
      case 'PIPE_TALLY':
        navigate(`/yard/pipe-tally?selected=${searchResult.id}`);
        break;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'INVENTORY_ITEM':
        return <Package className="h-5 w-5" />;
      case 'EQUIPMENT':
        return <Wrench className="h-5 w-5" />;
      case 'STORAGE_LOCATION':
        return <MapPin className="h-5 w-5" />;
      case 'PIPE_TALLY':
        return <Box className="h-5 w-5" />;
      default:
        return <Tag className="h-5 w-5" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'INVENTORY_ITEM':
        return 'Inventory Item';
      case 'EQUIPMENT':
        return 'Equipment';
      case 'STORAGE_LOCATION':
        return 'Storage Location';
      case 'PIPE_TALLY':
        return 'Pipe Tally';
      default:
        return type;
    }
  };

  const getTypeBadgeColor = (type: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
    switch (type) {
      case 'INVENTORY_ITEM':
        return 'default';
      case 'EQUIPMENT':
        return 'secondary';
      case 'STORAGE_LOCATION':
        return 'outline';
      case 'PIPE_TALLY':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getStatusBadgeVariant = (
    status: string
  ): 'default' | 'secondary' | 'outline' | 'destructive' => {
    if (['Optimal', 'AVAILABLE', 'IN_STOCK', 'ACTIVE'].includes(status)) return 'default';
    if (['Low', 'IN_USE', 'MAINTENANCE'].includes(status)) return 'secondary';
    if (['Stockout', 'UNAVAILABLE', 'REPAIR'].includes(status)) return 'destructive';
    return 'outline';
  };

  const renderSearchResult = () => {
    if (!searchResult) return null;

    const { type, details } = searchResult;
    // Type assertion for accessing details
    const data = details as Record<string, unknown>;

    return (
      <Card className="mt-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">{getTypeIcon(type)}</div>
              <div>
                <h3 className="text-base font-semibold text-foreground">{getTypeLabel(type)}</h3>
                <p className="text-sm text-muted-foreground">Found match for "{searchQuery}"</p>
              </div>
            </div>
            <Badge variant={getTypeBadgeColor(type) as any}>{type.replace('_', ' ')}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {type === 'INVENTORY_ITEM' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">SKU</p>
                  <p className="font-medium">{String(data.sku || '-')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{String(data.name || '-')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Category</p>
                  <p className="font-medium">{String(data.category || '-')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={getStatusBadgeVariant(String(data.status || ''))}>
                    {String(data.status || 'Unknown')}
                  </Badge>
                </div>
              </div>

              {Array.isArray(data.levels) && data.levels.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold mb-3">Stock by Location</h4>
                  <div className="space-y-2">
                    {(
                      data.levels as Array<{
                        id: string;
                        location?: { name: string };
                        quantity: number;
                        reservedQty?: number;
                      }>
                    ).map((level) => (
                      <div
                        key={level.id}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>{level.location?.name || 'Unknown'}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{level.quantity}</p>
                          {(level.reservedQty ?? 0) > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {level.reservedQty} reserved
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {type === 'EQUIPMENT' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Asset Number</p>
                  <p className="font-medium">{String(data.assetNumber || '-')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{String(data.name || '-')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={getStatusBadgeVariant(String(data.status || ''))}>
                    {String(data.status || 'Unknown')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Condition</p>
                  <Badge variant="outline">{String(data.condition || '-')}</Badge>
                </div>
              </div>
              {data.location !== undefined &&
                typeof data.location === 'object' &&
                data.location !== null && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{String((data.location as { name?: string }).name || 'Unknown')}</span>
                  </div>
                )}
              {data.serialNumber !== undefined && (
                <p className="text-sm text-muted-foreground">Serial: {String(data.serialNumber)}</p>
              )}
            </div>
          )}

          {type === 'STORAGE_LOCATION' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Code</p>
                  <p className="font-medium">{String(data.code || '-')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{String(data.name || '-')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <Badge variant="outline">{String(data.type || '-')}</Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={getStatusBadgeVariant(String(data.status || ''))}>
                    {String(data.status || 'Unknown')}
                  </Badge>
                </div>
              </div>
              {data.zone !== undefined && (
                <p className="text-sm text-muted-foreground">Zone: {String(data.zone)}</p>
              )}
              {Array.isArray(data.storedItems) && data.storedItems.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold mb-3">
                    Stored Items ({data.storedItems.length})
                  </h4>
                  <div className="space-y-2">
                    {(
                      data.storedItems as Array<{
                        id: string;
                        item?: { name: string };
                        quantity: number;
                      }>
                    )
                      .slice(0, 5)
                      .map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-2 bg-muted/50 rounded"
                        >
                          <span>{item.item?.name || 'Unknown'}</span>
                          <span className="font-medium">{item.quantity}</span>
                        </div>
                      ))}
                    {data.storedItems.length > 5 && (
                      <p className="text-sm text-muted-foreground text-center">
                        +{data.storedItems.length - 5} more items
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {type === 'PIPE_TALLY' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tally Number</p>
                  <p className="font-medium">{String(data.tallyNumber || '-')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pipe Type</p>
                  <p className="font-medium">{String(data.pipeType || '-')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Grade</p>
                  <Badge variant="outline">{String(data.grade || '-')}</Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={getStatusBadgeVariant(String(data.status || ''))}>
                    {String(data.status || 'Unknown')}
                  </Badge>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-2">
                <Card className="p-3 text-center">
                  <p className="text-2xl font-bold">{String(data.joints || 0)}</p>
                  <p className="text-xs text-muted-foreground">Joints</p>
                </Card>
                <Card className="p-3 text-center">
                  <p className="text-2xl font-bold">
                    {Number(data.totalFootage || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">Footage (ft)</p>
                </Card>
                <Card className="p-3 text-center">
                  <p className="text-2xl font-bold">
                    {String(data.outerDiameter || 0)}&quot; x {String(data.wallThickness || 0)}
                    &quot;
                  </p>
                  <p className="text-xs text-muted-foreground">OD x Wall</p>
                </Card>
              </div>
            </div>
          )}

          <Separator className="my-4" />

          <div className="flex justify-end">
            <Button onClick={navigateToDetail}>
              View Full Details
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Asset Search"
          description="Lookup across stock items, equipment, storage bins, and pipe tallies."
        />
      </motion.div>

      {/* Search Input */}
      <motion.div variants={fadeUp}>
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Enter barcode, SKU, asset number, serial number, or location code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="pl-10 pr-10 h-12 text-lg"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Dismiss"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={clearSearch}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <Button
                size="lg"
                onClick={() => handleSearch()}
                disabled={searching || !searchQuery.trim()}
              >
                {searching ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Search className="h-5 w-5" />
                )}
                <span className="ml-2">Search</span>
              </Button>
            </div>

            <div className="flex gap-2 mt-3">
              <Badge variant="outline" className="text-xs">
                <Package className="h-3 w-3 mr-1" /> SKU / Barcode
              </Badge>
              <Badge variant="outline" className="text-xs">
                <Wrench className="h-3 w-3 mr-1" /> Asset / Serial #
              </Badge>
              <Badge variant="outline" className="text-xs">
                <MapPin className="h-3 w-3 mr-1" /> Location Code
              </Badge>
              <Badge variant="outline" className="text-xs">
                <Box className="h-3 w-3 mr-1" /> Tally / Heat #
              </Badge>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Search Result */}
      <motion.div variants={fadeUp}>
        {renderSearchResult()}

        {/* Not Found */}
        {notFound && (
          <Card className="mt-6">
            <CardContent className="py-12 text-center">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Search className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-base font-semibold mb-2">No Results Found</h3>
              <p className="text-muted-foreground mb-4">
                No assets match "{searchQuery}". Try a different search term.
              </p>
              <div className="text-sm text-muted-foreground">
                <p>Search tips:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Check for typos in the barcode or SKU</li>
                  <li>Try searching by partial name or description</li>
                  <li>Use the exact asset number for equipment</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Searches */}
        {!searchResult && !notFound && recentSearches.length > 0 && (
          <Card className="mt-6">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <History className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-base font-semibold text-foreground">Recent Searches</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={clearSearchHistory}>
                  Clear History
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {recentSearches.map((search, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleRecentSearchClick(search.query)}
                    className="gap-2"
                  >
                    {search.resultType && getTypeIcon(search.resultType)}
                    {search.query}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Tips */}
        {!searchResult && !notFound && !searching && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <Card className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary-50 rounded-lg">
                  <Package className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold">Inventory Items</h4>
                  <p className="text-sm text-muted-foreground">
                    Search by SKU, barcode, or item name
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-success-50 rounded-lg">
                  <Wrench className="h-5 w-5 text-success-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold">Equipment</h4>
                  <p className="text-sm text-muted-foreground">
                    Search by asset number or serial number
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-status-neutral-subtle rounded-lg">
                  <MapPin className="h-5 w-5 text-status-neutral-foreground" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold">Storage Locations</h4>
                  <p className="text-sm text-muted-foreground">
                    Search by location code (e.g., A-01-03)
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-warning-50 rounded-lg">
                  <Box className="h-5 w-5 text-warning-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold">Pipe Tallies</h4>
                  <p className="text-sm text-muted-foreground">
                    Search by tally number or heat number
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}
      </motion.div>
    </motion.main>
  );
}
